import {
	OrderAction,
	OrderItemActionType,
	OrderPaymentStatus,
	SampleActionActor,
	useGetClientOrderQuery,
	type GetClientOrderQuery,
} from '@codezo/graphql';
import {
	OrderClient,
	OrderHeader,
	OrderDeliveries,
	OrderOverview,
	OrderProducts,
	OrderTotal,
	buildProgressRows,
	type OrderPaymentRow,
	type OrderProductFact,
	type OrderProductRow,
	type OrderTotalProps,
	type SummaryLine,
	type Tone,
} from '@codezo/order-detail';
import { FileGrid, FileTile, MicroLabel, Thumb, fileMeta } from '@codezo/quote-detail';
import { buildSpecificationRows } from '@codezo/product-specs';
import { ArrowLeft, ArrowUpRight, FileText } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { ProductImage, orderedProductImages } from '../../../components/ui/product-image';
import { FilePreview, type FilePreviewItem } from '../../../components/ui/file-preview';
import { OrderTransactions } from '../../../components/orders/OrderTransactions';
import { PayForFullOrderModal } from '../../../components/orders/PayForFullOrderModal';
import { PayForProductionModal } from '../../../components/orders/PayForProductionModal';
import { PayForSamplesModal } from '../../../components/orders/PayForSamplesModal';
import { OrderFlowSection } from './OrderFlowSection';
import { toDeliveryRows } from './deliveries';
import { ORDER_STATUS } from '../../../lib/status-configs';
import { formatDate } from '../../../lib/utils';
import { useCurrencyStore } from '../../../stores/currency';
import { useMoney } from './money';
import { useAuth } from '../../../hooks/useAuth';
import { readGuestScope } from '../../../lib/guest-token';
import { guestQuotePath } from '../../../lib/guest-session';

/**
 * Order detail, v2 — the client's side of the Flow C design.
 *
 * OPT-IN, AND V1 IS UNTOUCHED, exactly as on admin: this is `/orders/:id/v2`,
 * and `/orders/:id` still renders the existing page until QA signs off.
 *
 * ── The same modules, fewer facts ──
 *
 * Every section here is the same `@codezo/order-detail` component the admin page
 * renders. What differs is what gets passed: no margin, no supplier cost, no
 * confirm form. That is the whole point of the package contract — a read-only
 * surface passes no callbacks and omits the internal figures, and gets the
 * client's view for free rather than through a second set of components that
 * have to be kept in step.
 *
 * ── Where the money is stated ──
 *
 * The rail carries the ORDER-wide routes — all samples, the whole order up front
 * — and the spine carries the per-item ones, each priced by the server for that
 * product alone. They come from one field, `paymentOptions`, matched to a row by
 * `targetIds`; nothing on this page merges or adds them. Rows are alternatives,
 * and an empty list means an invoice is already open.
 */

type ClientOrderItem = NonNullable<GetClientOrderQuery['getClientOrder']>['items'][number];

/** Display tones for the payment statuses. An enum, so exhaustive. */
const PAYMENT_STATUS: Record<OrderPaymentStatus, { label: string; tone: Tone }> = {
	[OrderPaymentStatus.Awaiting]: { label: 'Awaiting', tone: 'warn' },
	[OrderPaymentStatus.Partial]: { label: 'Part paid', tone: 'info' },
	[OrderPaymentStatus.Settled]: { label: 'Settled', tone: 'success' },
	[OrderPaymentStatus.Waived]: { label: 'Waived', tone: 'neutral' },
	[OrderPaymentStatus.NotApplicable]: { label: 'None', tone: 'neutral' },
};

function DetailSkeleton() {
	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<div className="mb-5 h-4 w-16 animate-pulse rounded bg-surface-fill" />
			<div className="mb-2 h-7 w-48 animate-pulse rounded bg-surface-fill" />
			<div className="mb-7 h-4 w-72 animate-pulse rounded bg-surface-fill" />
			<div className="mb-7 h-[88px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface" />
			<div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_360px]">
				<div className="h-[260px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface" />
				<div className="h-[260px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface" />
			</div>
		</div>
	);
}

interface OrderDetailPageV2Props {
	/**
	 * Rendered inside the guest shell: the same page, minus the affordances that
	 * lead into a platform the guest isn't meant to browse.
	 *
	 * A guest session is a REAL session — it pays, approves samples and reads the
	 * same order — so nothing else here is conditioned on it. The difference is
	 * navigation: there is no orders list to go back to, and the quote lives at
	 * its guest path.
	 */
	guest?: boolean;
}

export function OrderDetailPageV2({ guest = false }: OrderDetailPageV2Props = {}) {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const { selectedCurrency } = useCurrencyStore();
	const money = useMoney();
	const { token } = useAuth();
	/**
	 * An admin previewing the client view can reach a real order.
	 *
	 * The backend allowlists exactly four mutations for a preview session —
	 * recording a quote view, saving an offer selection, refresh and logout — and
	 * refuses everything else. So this disables every control on the page that
	 * calls one: paying, and approving or declining a sample. Not just payments:
	 * a refused approve fails on click with nothing explaining why.
	 *
	 * Guest scope comes from the session, not from this tab — see `readGuestScope`.
	 */
	const previewReason =
		readGuestScope(token)?.preview === true ? 'Preview session — actions are disabled' : null;

	const [preview, setPreview] = useState<{ files: FilePreviewItem[]; index: number } | null>(
		null
	);

	/**
	 * The sample checkout: `true` for every payable sample (the rail's route), or
	 * a list of ids for one round opened from its own flow row.
	 */
	const [payingSamples, setPayingSamples] = useState<true | string[] | null>(null);
	/**
	 * Which products the production checkout is open for, or null.
	 *
	 * A list rather than a flag: the rail pays for everything currently payable,
	 * a flow row pays for its own product, and `createOrderPayment` is handed
	 * exactly the ids it is shown — so the figure on the button is what leaves the
	 * account.
	 */
	const [payingProducts, setPayingProducts] = useState<ClientOrderItem[] | null>(null);
	const [payingFullOrder, setPayingFullOrder] = useState(false);

	const { data, loading, networkStatus, refetch } = useGetClientOrderQuery({
		variables: { id: id!, inCurrency: selectedCurrency },
		skip: !id,
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	});

	// A payment landing changes two things that are fetched separately: the order
	// (its actions, its paid figures) and the transactions list, which has its own
	// query inside its own component. Refetching only the order left the list
	// showing the state before the payment until the page was reloaded.
	const [txnReloadKey, setTxnReloadKey] = useState(0);
	const handlePaid = () => {
		refetch();
		setTxnReloadKey((k) => k + 1);
	};

	const order = data?.getClientOrder;
	const isInitialLoading = loading && networkStatus === 1;

	// Just accepted an offer and landed here → open the payment modal the server
	// says applies. The flag arrives as navigation state from the accept flow and
	// is cleared immediately, so a refresh or a back-navigation never reopens it.
	// Runs once. Same behaviour as v1 — without it, accepting an offer drops the
	// client on an order page with the payment they were mid-way through making
	// sitting untouched in the rail.
	const autoOpenHandledRef = useRef(false);
	useEffect(() => {
		if (autoOpenHandledRef.current || !order) return;
		if (!(location.state as { autoOpenPayment?: boolean } | null)?.autoOpenPayment) return;
		autoOpenHandledRef.current = true;
		navigate(location.pathname, { replace: true, state: {} });

		// Nothing to open: the checkout would be refused on submit.
		if (previewReason) return;

		const clientActions =
			order.availableActions?.filter((a) => a.actor === SampleActionActor.Client) ?? [];
		const canPay = (action: OrderAction) =>
			clientActions.some((a) => a.action === action && !a.blocked);
		if (canPay(OrderAction.PaySamples)) setPayingSamples(true);
		else if (canPay(OrderAction.PayProducts))
			// Everything the server currently says is payable — the rail's own scope.
			setPayingProducts(
				(order.items ?? []).filter((item) =>
					(item.availableActions ?? []).some(
						(a) => a.action === OrderItemActionType.PayProduct && !a.blocked
					)
				)
			);
	}, [order, location.state, location.pathname, navigate, previewReason]);

	const progress = useMemo(
		() =>
			order
				? buildProgressRows({ items: order.items ?? [], shipments: order.shipments ?? [] })
				: [],
		[order]
	);

	if (isInitialLoading || !order) return <DetailSkeleton />;

	// Ordered by the field the server sends, same as admin. Nothing computed.
	const items = [...(order.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
	const summary = order.summary;
	const payment = order.paymentSummary;
	const total = summary?.grossAmount;
	const statusConfig = ORDER_STATUS[order.status] ?? { label: order.status, tone: 'neutral' };

	// Where it ships, as snapshotted when the order was accepted — so a later
	// edit to the quote's address can't rewrite where this one went. Assembled
	// rather than wrapped because the optional parts have to collapse without
	// leaving a blank line or a dangling comma.
	const a = order.deliveryAddress;
	const addressLines = a
		? [
				a.addressLine1,
				a.addressLine2,
				[a.postalCode, a.city].filter(Boolean).join(' '),
				[a.stateCode, a.countryCode].filter(Boolean).join(', '),
			].filter((line): line is string => Boolean(line && line.trim()))
		: [];

	const qtyHint =
		items.length > 1 && items.length <= 3
			? `${items.map((i) => i.quantity.toLocaleString()).join(' + ')} units`
			: `across ${items.length} ${items.length === 1 ? 'product' : 'products'}`;

	const selection: OrderTotalProps['selection'] = items.map((item) => ({
		id: item.id,
		kind: 'product' as const,
		name: item.productName || 'Product',
		sub: `${item.quantity.toLocaleString()} pcs`,
		total: money(item.lineTotal),
	}));

	/**
	 * Where the money stands. Every figure is a field — no column is derived from
	 * the other two, and the total line is the server's own rather than the two
	 * buckets added together, which its own docs explicitly warn against.
	 *
	 * A sample bucket reporting NOT_APPLICABLE is dropped rather than shown as
	 * zeros: "no samples were ordered" is not "nothing owed for samples yet".
	 */
	/**
	 * What has already been paid, as deductions from the order total.
	 *
	 * A bucket appears only once money has actually moved against it — an unpaid
	 * one is not a line here, it is what "Remaining to pay" is made of.
	 */
	/**
	 * The way to pay, whatever stage the order is at.
	 *
	 * Two routes while nothing is paid — samples now, or the whole order up front —
	 * and a single button afterwards for whatever is currently payable. The server
	 * decides which of the three it offers; this only renders what it is handed,
	 * with its own label and its own blocked reason.
	 */
	const paySamples = (order.availableActions ?? []).find(
		(a) => a.action === OrderAction.PaySamples
	);
	const payFullOrder = (order.availableActions ?? []).find(
		(a) => a.action === OrderAction.PayFullOrder
	);
	const payProducts = (order.availableActions ?? []).find(
		(a) => a.action === OrderAction.PayProducts
	);

	/**
	 * The items this payment would actually cover.
	 *
	 * PAY_PRODUCTS means "everything payable right now", not everything unpaid —
	 * a product whose sample is not approved yet is not in it. The per-item
	 * PAY_PRODUCT action is the server saying which, so the modal is handed
	 * exactly those: `createOrderPayment` refuses the whole call if anything in it
	 * is not payable, rather than charging for part of the selection.
	 */
	const payableItems = items.filter((item) =>
		(item.availableActions ?? []).some(
			(a) => a.action === OrderItemActionType.PayProduct && !a.blocked
		)
	);

	const payAction =
		paySamples || payFullOrder ? (
			<div className="flex flex-col gap-2">
				{[
					{ action: paySamples, onClick: () => setPayingSamples(true), primary: false },
					{
						action: payFullOrder,
						onClick: () => setPayingFullOrder(true),
						primary: true,
					},
				]
					.filter((o) => o.action)
					.map((o) => (
						// Label left, amount right — centred, the two amounts never
						// line up with each other, and a price is what the eye is
						// comparing when it is choosing between two routes.
						<Button
							key={o.action!.action}
							variant={o.primary ? 'default' : 'outline'}
							className="w-full justify-between"
							disabled={!!o.action!.blocked || !!previewReason}
							title={previewReason ?? o.action!.blockedReason ?? undefined}
							onClick={o.onClick}
						>
							<span>{o.action!.label}</span>
							{o.action!.amount != null && (
								<span className="font-mono">
									{money(o.action!.amount, o.action!.currency)}
								</span>
							)}
						</Button>
					))}
				{/* Why the up-front route is worth choosing, said once and only where
				    it is offered.

				    Both claims are the server's, not sales copy. One invoice: the
				    full-order option covers samples and production together, which is
				    why it is never blocked by an unpaid sample the way a product row
				    is. Nothing waits: every "start production" action on this order
				    arrives blocked with "payment is required before production can
				    start", so a payment made later is a pause later.

				    Deliberately not promising that approval is unaffected. Paying up
				    front settles production before the samples have been reviewed, and
				    a reassurance we cannot stand behind does not belong on a button
				    that moves this much money. */}
				{payFullOrder && !payFullOrder.blocked && (
					<div className="text-2xs leading-snug text-text-3">
						<span className="text-text-2">Paying up front keeps it moving:</span> one
						invoice for samples and production, and nothing later waits on a payment
						clearing.
					</div>
				)}
				{/* Every figure in the column above this is net until the Total line;
				    an action's `amount` is VAT-inclusive. Unsaid, that turns a DKK 700
				    sample into a DKK 875 button with nothing on the page connecting
				    them, and the reader is left to guess whether it is the same money. */}
				<div className="text-2xs text-text-4">Amounts include VAT.</div>
				{previewReason && <div className="text-2xs text-status-warn">{previewReason}</div>}
			</div>
		) : payProducts ? (
			<>
				<Button
					variant="outline"
					className="w-full justify-between"
					disabled={!!payProducts.blocked || !!previewReason}
					title={previewReason ?? payProducts.blockedReason ?? undefined}
					onClick={() => setPayingProducts(payableItems)}
				>
					<span>{payProducts.label}</span>
					{/* The ACTION's amount, not what is left on the order. This pays
					    for whatever is payable today, which on an order with an
					    unapproved sample is less than the remainder — quoting the
					    remainder promised a payment the mutation would refuse. */}
					<span className="font-mono">
						{money(payProducts.amount, payProducts.currency)}
					</span>
				</Button>
				{payProducts.blocked && payProducts.blockedReason ? (
					<div className="mt-1.5 text-2xs text-status-warn">
						{payProducts.blockedReason}
					</div>
				) : (
					/* A primary button with a figure on it reads as the next required
					   step. On the samples route it is not: each product is invoiced
					   when ITS sample is approved, and waiting is the normal path. The
					   count says how much of the order this covers, so an order where
					   one sample is still open doesn't look like the whole thing is
					   due. */
					<div className="mt-1.5 text-2xs leading-snug text-text-4">
						{payProducts.itemCount != null && payProducts.itemCount < items.length
							? `${payProducts.itemCount} of ${items.length} products are ready to pay for. `
							: null}
						There is no rush — each product is invoiced once you have approved its
						sample. Amounts include VAT.
					</div>
				)}
			</>
		) : undefined;

	/**
	 * Which products a paid bucket actually covers.
	 *
	 * `paymentSummary` splits money into two order-level buckets — every sample,
	 * and all production — so a row reading "Sample payment · DKK 875" on a
	 * two-product order says nothing about WHICH sample was paid for. On a
	 * part-paid bucket that is the first thing anyone asks.
	 *
	 * Read off `paidAt`, which the server sets on the thing that was paid: on the
	 * sample round for a sample invoice, on the item for a production one. Not a
	 * split of the amount — the figure stays the bucket's own — just the names of
	 * what is behind it.
	 */
	const covered = (paid: (item: (typeof items)[number]) => boolean) => {
		const names = items.filter(paid).map((item) => item.productName || 'Product');
		if (names.length === 0) return undefined;
		return names.length === items.length ? 'All products' : names.join(' · ');
	};

	const paidRows: OrderPaymentRow[] = payment
		? [
				...(payment.samplePaidAmount > 0
					? [
							{
								key: 'samples',
								label: 'Sample payment',
								status: PAYMENT_STATUS[payment.sampleStatus],
								amount: money(payment.samplePaidAmount),
								// Which samples. The credit note that used to sit here is
								// gone: the Samples line above already explains the
								// charge-and-credit in full, and saying it twice in one
								// card left the row with a caveat where its subject
								// should be.
								note: covered((item) => !!item.samples?.some((s) => s.paidAt)),
							},
						]
					: []),
				...(payment.productPaidAmount > 0
					? [
							{
								key: 'products',
								label: 'Production payment',
								status: PAYMENT_STATUS[payment.productStatus],
								amount: money(payment.productPaidAmount),
								note: covered((item) => !!item.paidAt),
							},
						]
					: []),
			]
		: [];

	const summaryLines: SummaryLine[] = summary
		? [
				{
					label: 'Products subtotal',
					value: money(summary.productsSubtotal),
					group: 'products',
				},
				{
					label: 'Product delivery',
					value: money(summary.deliveryTotal),
					group: 'products',
				},
				// A PREPAID sample is charged up front and credited back in full on
				// the production invoice, so it contributes NOTHING to order value —
				// which is why the API keeps it out of `samplesTotal` and this line
				// reads zero while "Pay for samples" asks for real money.
				//
				// It cannot lead with "charged up front": every sample is a
				// prepayment, PREPAID ones included, so that describes both while
				// naming one's amount. What tells them apart is the credit.
				{
					label: 'Samples',
					value: money(summary.samplesTotal),
					group: 'samples',
					// The note names the SAME figure the pay button does.
					//
					// `samplesPrepaidTotal` is the credited GOODS, net — 700 on this
					// order. The button charges the sample invoice, GROSS — 875.
					// Printing the net one here put two numbers for one sample within a
					// few centimetres of each other and left the reader to work out that
					// one was the other plus VAT. `sampleTotalAmount` is what is
					// actually charged up front — goods and their freight, gross, the
					// same basis as the button and the invoice — so the two agree on
					// sight.
					//
					// It stays a note rather than a line of its own: it is credited back
					// in full, so a row in this column would appear to add to a total it
					// contributes nothing to. Which is also why the note has to say the
					// charge is PART of that total. "It adds nothing to your order
					// total" was true of the line and invited the opposite conclusion
					// about the button — that the 875 was on top of the 5,306.35.
					note:
						summary.samplesPrepaidTotal > 0 ? (
							<div className="text-2xs leading-snug text-text-4">
								{money(payment?.sampleTotalAmount ?? summary.samplesPrepaidTotal)}{' '}
								incl. VAT is charged up front, then credited back on the production
								invoice — which is why this line is zero. It is part of the total
								below, not extra.
							</div>
						) : undefined,
				},
				{
					label: 'Sample delivery',
					value: money(summary.samplesShippingTotal),
					group: 'samples',
				},
				...(summary.fees.length > 0
					? summary.fees.map((fee) => ({
							label: fee.name,
							value: money(fee.amount),
							group: 'adjustments',
							emphasis: 'fee' as const,
						}))
					: [{ label: 'Fees', value: null, group: 'adjustments' }]),
				...(summary.discounts.length > 0
					? summary.discounts.map((discount) => ({
							label: discount.name,
							value: money(discount.amount),
							group: 'adjustments',
							emphasis: 'discount' as const,
						}))
					: [{ label: 'Discounts', value: null, group: 'adjustments' }]),
			]
		: [];

	const products: OrderProductRow[] = items.map((item) => {
		const images = orderedProductImages(item.productImages);
		const attachments = item.attachments ?? [];
		const imageFiles: FilePreviewItem[] = images.map((img, i) => ({
			id: img.url,
			url: img.url,
			name: img.altText || img.caption || `${item.productName || 'Product'} ${i + 1}`,
			mimeType: 'image/*',
		}));

		return {
			id: item.id,
			name: item.productName || 'Product',
			sku: item.productSku,
			description: item.productDescription,
			specs: buildSpecificationRows({
				specifications: item.productSpecifications,
				dimensions: item.productDimensions,
				weight: item.productWeight,
			}).map((row) => [row.label, row.value] as OrderProductFact),
			media: images.map((img, i) => (
				<ProductImage
					key={img.url}
					image={img}
					alt={i === 0 ? item.productName || '' : ''}
					className="h-full w-full"
				/>
			)),
			onOpenImages: (index) => setPreview({ files: imageFiles, index }),
			note: item.productSpecificationsAt
				? `Specifications as agreed when this order was accepted ${formatDate(
						item.productSpecificationsAt
					)}`
				: undefined,
			files: attachments.length > 0 && (
				<FileGrid>
					{attachments.map((file, i) => (
						<FileTile
							key={file.id}
							name={file.name}
							meta={fileMeta(file.name, file.mimeType, file.size)}
							description={file.description}
							onOpen={() =>
								setPreview({
									files: attachments.map((f) => ({
										id: f.id,
										name: f.name,
										url: f.url,
										mimeType: f.mimeType,
										size: f.size,
									})),
									index: i,
								})
							}
						/>
					))}
				</FileGrid>
			),
			filesEmpty: 'No files on this product.',
			// What the client is paying, and how it adds up: quantity × unit price
			// = the line total below. The unit price was missing, which left the
			// card stating a quantity and a total with nothing connecting them —
			// the one figure a client checks a quote against.
			//
			// Still no base price and no margin: those are what the studio pays and
			// what it makes. The client app simply doesn't pass them, and the card
			// renders what it gets.
			facts: [
				['Qty', `${item.quantity.toLocaleString()} units`],
				['Unit price', money(item.unitPrice, null, { unit: true }) ?? '—'],
			],
			total: ['Line total', money(item.lineTotal) ?? '—'],
		};
	});

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			{/* A guest has no orders list to go back to — the link would land them
			    on a platform their session cannot browse. */}
			{!guest && (
				<button
					onClick={() => navigate('/orders')}
					className="mb-4 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
				>
					<ArrowLeft size={14} />
					Orders
				</button>
			)}

			<OrderHeader
				name={order.name}
				status={{ label: statusConfig.label, tone: statusConfig.tone }}
				subtitle={
					<>
						<span className="font-mono">{order.orderNumber}</span>
						{order.poNumber && (
							<>
								<span className="px-1.5 text-text-4">&middot;</span>
								PO {order.poNumber}
							</>
						)}
						{order.createdAt && (
							<>
								<span className="px-1.5 text-text-4">&middot;</span>
								Ordered {formatDate(order.createdAt)}
							</>
						)}
					</>
				}
			/>

			<div className="mt-5 mb-7">
				<OrderOverview
					progress={progress}
					stats={[
						{ label: 'Items', value: String(items.length) },
						{
							label: 'Total qty',
							value: (order.totalQuantity ?? 0).toLocaleString(),
							hint: qtyHint,
						},
						{
							label: 'Order total',
							value: money(total) ?? '—',
							hint: 'incl. VAT',
						},
						{
							label: 'Paid',
							value: money(payment?.totalPaidAmount) ?? '—',
							// The denominator is back: `paymentSummary` is now the
							// invoice basis — gross, freight included — so this and the
							// order total answer the same question.
							hint: `of ${money(payment?.totalAmount)} invoiced`,
						},
					]}
				/>
			</div>

			<div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_360px] lg:items-start">
				<div className="flex min-w-0 flex-col gap-7">
					{/* A flow row pays for ITS product and is handed exactly that id;
					    the rail's route pays for everything currently payable. Both
					    come from `paymentOptions`, priced by the server with the same
					    sample credit `paymentSummary` uses — so the itemised rows sum
					    to the order total rather than over-quoting it. */}
					<OrderFlowSection
						order={order}
						onChanged={refetch}
						previewReason={previewReason}
						onPayProduct={(item) => setPayingProducts([item])}
						onPaySample={(sampleId) => setPayingSamples([sampleId])}
					/>
					<OrderProducts
						products={products}
						meta="what was agreed when you accepted — specifications are frozen"
					/>
					<section>
						<div className="mb-3">
							<h2 className="text-xs font-medium uppercase tracking-[0.14em] text-text-3">
								Transactions
							</h2>
						</div>
						<OrderTransactions orderId={order.id} reloadKey={txnReloadKey} />
					</section>
				</div>

				<div className="flex flex-col gap-5 lg:sticky lg:top-6">
					{/* What this order was agreed on.

					    The same card the studio's page gives the quote, and the same
					    one it gives the client — `OrderClient` is `QuoteClient`
					    aliased, so the anatomy is shared rather than matched by hand.

					    First in the rail, as it is on the studio's page — where it sits
					    beside the client card, because "who is this for" and "what was
					    it agreed on" are the same kind of question. There is no client
					    card here, the client being the viewer, so this leads: what the
					    order was agreed on, then what it costs, then what is coming.

					    No quote NAME: `ClientOrder` carries `orderQuoteId` but no
					    `orderQuote` relation, so there is nothing to title it with. It
					    does not need one — a client has exactly one quote per order and
					    is not choosing between them. */}
					<OrderClient
						name="Your quote"
						meta="What this order was agreed on"
						avatar={
							<Thumb size={34}>
								<FileText size={15} />
							</Thumb>
						}
						action={
							<Button
								variant="outline"
								size="sm"
								// Same quote, addressed the way this session can reach
								// it: a guest's copy lives under /guest, and the
								// platform route would bounce them.
								onClick={() =>
									navigate(
										guest
											? guestQuotePath(order.orderQuoteId)
											: `/quotes/${order.orderQuoteId}`
									)
								}
							>
								Open quote
								<ArrowUpRight size={14} data-icon="inline-end" />
							</Button>
						}
					/>

					<OrderTotal
						reference={order.orderNumber}
						selection={selection}
						lines={summaryLines}
						subtotal={money(summary?.grandTotal)}
						vatLabel={`VAT (${summary?.vatRate ?? 0}%)`}
						vat={money(summary?.vatAmount)}
						total={money(total)}
						totalNote="incl. VAT"
						payments={
							payment
								? {
										paid: paidRows,
										remaining: money(payment.totalOutstandingAmount),
										// Three fields put in a row, not a calculation.
										// Every version of this block without it drew the
										// same question: do these numbers add up?
										equation: `${money(payment.totalAmount)} order total − ${money(
											payment.totalPaidAmount
										)} paid = ${money(payment.totalOutstandingAmount)} remaining`,
										action: payAction,
									}
								: undefined
						}
						// No `margin`: the client never sees it, and the row simply
						// doesn't render. The module isn't deciding that — this page is.
					/>
					{/* Delivery only. No activity feed on the client page: the
					    timeline is a record of what the STUDIO did — items confirmed,
					    a sample's payment treatment changed, a shipment corrected —
					    and OrderActivityAction's values are almost entirely internal.
					    The spine already tells a client where their products are,
					    which is the half of it that concerns them. */}
					<OrderDeliveries
						deliveries={toDeliveryRows(order.deliveries ?? [])}
						emptyState="Nothing is on its way yet — we will show each sample and shipment here as it is made."
						note={
							addressLines.length > 0 ? (
								<>
									<MicroLabel className="mb-1 block">Shipping to</MicroLabel>
									<div className="text-sm leading-relaxed text-text-2">
										{addressLines.map((line) => (
											<div key={line}>{line}</div>
										))}
									</div>
								</>
							) : undefined
						}
					/>
				</div>
			</div>

			{/* Mounted only while open: both seed their state from the items on
			    first render, and those change with every payment. */}
			{payingSamples && (
				<PayForSamplesModal
					open
					onOpenChange={(open) => {
						if (!open) setPayingSamples(null);
					}}
					orderId={order.id}
					items={items}
					// Undefined for the rail's all-samples route; the server then
					// charges everything currently payable.
					sampleIds={payingSamples === true ? undefined : payingSamples}
					paymentMethods={order.availableSamplePaymentMethods}
					orderPoNumber={order.poNumber}
					onSuccess={handlePaid}
				/>
			)}
			{payingFullOrder && (
				<PayForFullOrderModal
					open
					onOpenChange={setPayingFullOrder}
					orderId={order.id}
					paymentMethods={order.availablePaymentMethods}
					orderPoNumber={order.poNumber}
					onSuccess={handlePaid}
				/>
			)}

			{/* Everything currently payable — the widget pays what is remaining,
			    which is the figure beside its button. */}
			{payingProducts && (
				<PayForProductionModal
					open
					onOpenChange={(open) => {
						if (!open) setPayingProducts(null);
					}}
					orderId={order.id}
					items={payingProducts}
					paymentMethods={order.availablePaymentMethods}
					orderPoNumber={order.poNumber}
					onSuccess={handlePaid}
				/>
			)}

			{preview && (
				<FilePreview
					files={preview.files}
					initialIndex={preview.index}
					onClose={() => setPreview(null)}
				/>
			)}
		</div>
	);
}
